import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel'
import { ReportDataWrapper } from '#components/ReportDataWrapper'
import { ReportFilterControls } from '#components/ReportFilterControls'
import { StatCard } from '#components/StatCard'
import { useFetch } from '#hooks/useAdminResource'
import { useTrackingFilters } from '#hooks/useTrackingFilters'
import { formatPercentage } from '#utils/format'

interface DocumentStats {
  totalDocuments: number
  byStatus: {
    status: string
    count: number
    percentage: number
  }[]
  carrierStatusBreakdown: {
    carrierCode: string
    total: number
    byStatus: {
      status: string
      count: number
    }[]
  }[]
  byAccount: {
    accountId: string
    count: number
    percentage: number
  }[]
  recentActivity: {
    date: string
    count: number
    byStatus: {
      status: string
      count: number
    }[]
  }[]
}

const getStatusCount = (
  items: { status: string; count: number }[],
  status: string,
) => items.find(s => s.status === status)?.count || 0

const getErrorCount = (items: { status: string; count: number }[]) =>
  getStatusCount(items, 'error') + getStatusCount(items, 'not-found')

const StatusColumns = ({
  byStatus,
}: {
  byStatus: { status: string; count: number }[]
}) => {
  const errorCount = getErrorCount(byStatus)

  return (
    <>
      <td className="text-right text-success">
        {getStatusCount(byStatus, 'ok').toLocaleString()}
      </td>
      <td className="text-right text-error">
        {errorCount > 0 ? errorCount.toLocaleString() : '-'}
      </td>
    </>
  )
}

const statusColumnHeaders = (
  <>
    <th className="text-right">OK</th>
    <th className="text-right">Error/Not Found</th>
  </>
)

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ok':
      return 'text-success'
    case 'error':
    case 'not-found':
      return 'text-error'
    default:
      return 'text-base-content'
  }
}

const DocumentReporting = () => {
  const { localFilters, setLocalFilters, apiFilters } = useTrackingFilters()

  const {
    data: stats,
    isLoading,
    error,
    refetch: refresh,
  } = useFetch<DocumentStats>('documents/report', apiFilters)

  return (
    <ReportDataWrapper
      isLoading={isLoading}
      error={error}
      loadingMessage="Loading document report..."
      onRetry={() => refresh()}
    >
      <div className="space-y-6">
        <AdminFilters isLoading={isLoading} onRefresh={refresh}>
          <ReportFilterControls
            filters={localFilters}
            onChange={setLocalFilters}
          />
        </AdminFilters>

        {stats && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard
                title="Total Documents"
                value={stats.totalDocuments.toLocaleString()}
                color="text-primary"
              />
              <StatCard
                title="OK"
                value={getStatusCount(stats.byStatus, 'ok')}
                color="text-success"
              />
              <StatCard
                title="Not Found"
                value={getStatusCount(stats.byStatus, 'not-found')}
                color="text-warning"
              />
              <StatCard
                title="Errors"
                value={getStatusCount(stats.byStatus, 'error')}
                color="text-error"
              />
            </div>

            {/* Status Breakdown */}
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title">Document Status Breakdown</h2>
                <div className="overflow-x-auto">
                  <table className="table table-zebra">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th className="text-right">Count</th>
                        <th className="text-right">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.byStatus.map(item => (
                        <tr key={item.status}>
                          <td>
                            <span
                              className={`font-medium ${getStatusColor(item.status)}`}
                            >
                              {item.status.toUpperCase().replace(/-/g, ' ')}
                            </span>
                          </td>
                          <td className="text-right">
                            {item.count.toLocaleString()}
                          </td>
                          <td className="text-right">
                            {item.percentage.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Top Carriers by Document Volume */}
            {stats.carrierStatusBreakdown?.length > 0 && (
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">
                    Top Carriers by Document Volume
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="table table-zebra">
                      <thead>
                        <tr>
                          <th>Carrier Code</th>
                          <th className="text-right">Total</th>
                          <th className="text-right">%</th>
                          {statusColumnHeaders}
                        </tr>
                      </thead>
                      <tbody>
                        {stats.carrierStatusBreakdown.map(item => (
                          <tr key={item.carrierCode}>
                            <td className="font-mono font-medium uppercase">
                              {item.carrierCode}
                            </td>
                            <td className="text-right font-semibold">
                              {item.total.toLocaleString()}
                            </td>
                            <td className="text-right">
                              {formatPercentage(
                                item.total,
                                stats.totalDocuments,
                              )}
                              %
                            </td>
                            <StatusColumns byStatus={item.byStatus} />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Timeline */}
            {stats.recentActivity.length > 0 && (
              <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">Daily Activity by Status</h2>
                  <div className="overflow-x-auto">
                    <table className="table table-zebra">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="text-right">Total</th>
                          {statusColumnHeaders}
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recentActivity.map(item => (
                          <tr key={item.date}>
                            <td>{item.date}</td>
                            <td className="text-right font-semibold">
                              {item.count.toLocaleString()}
                            </td>
                            <StatusColumns byStatus={item.byStatus} />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!stats && !isLoading && !error && (
          <div className="alert">
            <span>No document data available for the selected date range.</span>
          </div>
        )}
      </div>
    </ReportDataWrapper>
  )
}

export { DocumentReporting }
